import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import { getFiscalInvoiceById } from "../redux/actions/fiscalActions";

import FiscalInvoiceDetailsTab from "../components/fiscal/invoices/FiscalInvoiceDetailsTab";
import FiscalInvoicePdfTab from "../components/fiscal/invoices/FiscalInvoicePdfTab";
import AppCircularProgress from "../components/common/AppCircularProgress";
import StatusChip from "../components/common/StatusChip";

const FiscalInvoicePage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { closeCurrentAndNavigate } = useWorkspaceNavigation();

  const {
    currentFiscalInvoice,
    loadingFiscalInvoice,
    errorLoadingFiscalInvoice,
  } = useSelector((state) => state.fiscal);

  const [activeTab, setActiveTab] = useState(0);
  const [invoiceNumber, setInvoiceNumber] = useState(null);

  useEffect(() => {
    // Extract ID/INV number from URL
    const paths = location.pathname.split("/");
    const id = paths[paths.length - 1];
    if (id && id !== "invoices") setInvoiceNumber(id);
  }, [location.pathname]);

  useEffect(() => {
    if (invoiceNumber) dispatch(getFiscalInvoiceById(invoiceNumber));
  }, [dispatch, invoiceNumber]);

  if (loadingFiscalInvoice) return <AppCircularProgress />;
  if (errorLoadingFiscalInvoice)
    return (
      <Box p={3}>
        <Alert severity="error">{errorLoadingFiscalInvoice}</Alert>
      </Box>
    );
  if (!currentFiscalInvoice) return null;

  const fiscalData = currentFiscalInvoice.fiscalData;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={() => closeCurrentAndNavigate("/fiscal/invoices")}
              color="inherit"
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              eTIMS Invoice #{fiscalData.invoiceNo}
            </Typography>
            {!isMobile && <StatusChip status={fiscalData.status} />}
          </Stack>
        </Stack>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: { xs: 0, sm: 6 }, mb: 2 }}
        >
          Local Ref: {fiscalData.traderSystemInvoiceNumber} • Issued:{" "}
          {new Date(fiscalData.invoiceDate).toLocaleString()}
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Fiscal Invoice Details" />
          <Tab label="PDF Document" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 3 } }}>
        {activeTab === 0 && (
          <FiscalInvoiceDetailsTab invoice={currentFiscalInvoice} />
        )}
        {activeTab === 1 && (
          <FiscalInvoicePdfTab invoiceNumber={invoiceNumber} />
        )}
      </Box>
    </Box>
  );
};
export default FiscalInvoicePage;
